import { useCallback, useState } from 'react';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Eye, EyeOff, GripVertical, Loader2, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { Modal } from '@/components/Modal';
import { Tabs } from '@/components/Tabs';

import type { ColumnConfig, ColumnConfigModalProps, ColumnDefinition } from './types';

/**
 * Modal "Konfiguracja tabeli" — port z SellCall.
 *
 * Dwie zakladki:
 *  - Kolumny (zawsze widoczna): drag-drop reorder, toggle widocznosci,
 *    edycja etykiety, edycja szerokosci.
 *  - Layouty (tylko admin): switcher, tworzenie nowych, usuwanie.
 *
 * Adaptacje vs SellCall:
 *  - Lista rol w "Nowy layout" zostaje hardcoded jak w SellCall (placeholder
 *    do Kroku 5 — tam wczytamy realne role z `role_templates` tenanta).
 */
export function ColumnConfigModal<T>({
  columns,
  columnConfigs,
  onConfigChange,
  onSave,
  onReset,
  onClose,
  availableLayouts,
  currentLayoutId,
  onSwitchLayout,
  onCreateLayout,
  onDeleteLayout,
  isAdmin,
  isSaving = false,
}: ColumnConfigModalProps<T>) {
  const [activeTab, setActiveTab] = useState<'columns' | 'layouts'>('columns');
  const [newLayoutName, setNewLayoutName] = useState('');
  const [newLayoutRoles, setNewLayoutRoles] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Get column definition by ID
  const getColumn = useCallback(
    (id: string): ColumnDefinition<T> | undefined => {
      return columns.find((c) => c.id === id);
    },
    [columns],
  );

  // Sort configs by order
  const sortedConfigs = [...columnConfigs].sort((a, b) => a.order - b.order);

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = sortedConfigs.findIndex((c) => c.id === active.id);
        const newIndex = sortedConfigs.findIndex((c) => c.id === over.id);

        const newSorted = arrayMove(sortedConfigs, oldIndex, newIndex);

        // Reassign order values
        const reordered = newSorted.map((config, index) => ({
          ...config,
          order: index,
        }));

        onConfigChange(reordered);
      }
    },
    [sortedConfigs, onConfigChange],
  );

  // Toggle visibility
  const toggleVisibility = useCallback(
    (columnId: string) => {
      onConfigChange(
        columnConfigs.map((c) => (c.id === columnId ? { ...c, visible: !c.visible } : c)),
      );
    },
    [columnConfigs, onConfigChange],
  );

  // Update custom label
  const updateLabel = useCallback(
    (columnId: string, label: string) => {
      onConfigChange(
        columnConfigs.map((c) =>
          c.id === columnId ? { ...c, customLabel: label || undefined } : c,
        ),
      );
    },
    [columnConfigs, onConfigChange],
  );

  // Update width
  const updateWidth = useCallback(
    (columnId: string, width: string) => {
      const numWidth = parseInt(width);
      onConfigChange(
        columnConfigs.map((c) =>
          c.id === columnId ? { ...c, width: isNaN(numWidth) ? width || 'auto' : numWidth } : c,
        ),
      );
    },
    [columnConfigs, onConfigChange],
  );

  // Handle create layout
  const handleCreateLayout = useCallback(async () => {
    if (!newLayoutName.trim() || !onCreateLayout) return;

    setIsCreating(true);
    try {
      await onCreateLayout(
        newLayoutName.trim(),
        newLayoutRoles.length > 0 ? newLayoutRoles : undefined,
      );
      setNewLayoutName('');
      setNewLayoutRoles([]);
    } finally {
      setIsCreating(false);
    }
  }, [newLayoutName, newLayoutRoles, onCreateLayout]);

  const columnsContent = (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={sortedConfigs.map((c) => c.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-2">
          {sortedConfigs.map((config) => {
            const column = getColumn(config.id);
            if (!column) return null;

            return (
              <SortableColumnItem
                key={config.id}
                config={config}
                column={column}
                onToggleVisibility={() => toggleVisibility(config.id)}
                onUpdateLabel={(label) => updateLabel(config.id, label)}
                onUpdateWidth={(width) => updateWidth(config.id, width)}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );

  /* Layouts tab - for admins */
  const layoutsContent = (
    <div className="space-y-4">
      {/* Layout selector */}
      {availableLayouts.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Aktywny layout</label>
          <Dropdown
            options={availableLayouts.map((layout) => ({
              value: layout.id,
              label: `${layout.name}${layout.isDefault ? ' (domyślny)' : ''}${
                layout.roleIds?.length ? ` - role: ${layout.roleIds.join(', ')}` : ''
              }`,
            }))}
            value={currentLayoutId || ''}
            onChange={(val) => onSwitchLayout(val)}
            aria-label="Aktywny layout"
          />
        </div>
      )}

      {/* Existing layouts */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">Zapisane layouty</label>
        {availableLayouts.map((layout) => (
          <div
            key={layout.id}
            className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
              layout.id === currentLayoutId
                ? 'border-accent-500 bg-accent-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div>
              <p className="font-medium text-gray-900">{layout.name}</p>
              <div className="flex items-center gap-2 mt-1">
                {layout.isDefault && (
                  <span className="text-xs px-2 py-0.5 bg-accent-100 text-accent-700 rounded">
                    Domyślny
                  </span>
                )}
                {layout.roleIds && layout.roleIds.length > 0 && (
                  <span className="text-xs text-gray-500">Role: {layout.roleIds.join(', ')}</span>
                )}
              </div>
            </div>
            {!layout.isDefault && onDeleteLayout && (
              <button
                onClick={() => onDeleteLayout(layout.id)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Usuń layout"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Create new layout */}
      {onCreateLayout && (
        <div className="pt-4 border-t">
          <h4 className="font-medium text-gray-700 mb-3">Nowy layout</h4>
          <div className="space-y-3">
            <input
              type="text"
              value={newLayoutName}
              onChange={(e) => setNewLayoutName(e.target.value)}
              placeholder="Nazwa layoutu"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
            />
            <div>
              <label className="text-sm font-medium text-gray-700">
                Przypisz do ról (opcjonalnie):
              </label>
              <div className="flex flex-wrap gap-3 mt-2">
                {/* TODO Krok 5: zaciągnąć realne role z `role_templates` tenanta. */}
                {['admin', 'consultant'].map((role) => (
                  <label key={role} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newLayoutRoles.includes(role)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewLayoutRoles([...newLayoutRoles, role]);
                        } else {
                          setNewLayoutRoles(newLayoutRoles.filter((r) => r !== role));
                        }
                      }}
                      className="rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                    />
                    <span className="text-sm text-gray-700">{role}</span>
                  </label>
                ))}
              </div>
            </div>
            <button
              onClick={handleCreateLayout}
              disabled={!newLayoutName.trim() || isCreating}
              className="flex items-center gap-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isCreating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Utwórz layout
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      fixedHeight
      title="Konfiguracja tabeli"
      testId="column-config-modal"
      // Zmiany kolumn działają na tabelę na żywo (a „Anuluj" ich nie cofa), więc bez pytania o
      // porzucenie. Zapis w toku blokuje tło i Esc.
      closeOnBackdrop={!isSaving}
      closeOnEsc={!isSaving}
      footer={
        <div className="flex w-full items-center justify-between">
          <Button variant="ghost" onClick={onReset} leftIcon={<RotateCcw className="h-4 w-4" />}>
            Resetuj
          </Button>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Anuluj
            </Button>
            <Button onClick={onSave} loading={isSaving} leftIcon={<Save className="h-4 w-4" />}>
              Zapisz
            </Button>
          </div>
        </div>
      }
    >
      <Tabs
        idPrefix="column-config"
        value={activeTab}
        onChange={(v) => setActiveTab(v as 'columns' | 'layouts')}
        items={[
          {
            value: 'columns',
            label: 'Kolumny',
            testId: 'column-config-tab-columns',
            content: columnsContent,
          },
          ...(isAdmin
            ? [
                {
                  value: 'layouts',
                  label: 'Layouty',
                  testId: 'column-config-tab-layouts',
                  content: layoutsContent,
                },
              ]
            : []),
        ]}
      />
    </Modal>
  );
}

// Sortable column item component
interface SortableColumnItemProps<T> {
  config: ColumnConfig;
  column: ColumnDefinition<T>;
  onToggleVisibility: () => void;
  onUpdateLabel: (label: string) => void;
  onUpdateWidth: (width: string) => void;
}

function SortableColumnItem<T>({
  config,
  column,
  onToggleVisibility,
  onUpdateLabel,
  onUpdateWidth,
}: SortableColumnItemProps<T>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: config.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const canHide = column.hideable !== false;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg ${
        isDragging ? 'shadow-lg bg-white dark:bg-gray-600 z-10' : ''
      }`}
    >
      {/* Drag handle */}
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className="w-5 h-5 text-gray-400" />
      </div>

      {/* Visibility toggle */}
      <button
        onClick={onToggleVisibility}
        disabled={!canHide}
        className={`p-1.5 rounded transition-colors ${
          config.visible ? 'text-accent-600 hover:bg-accent-50' : 'text-gray-400 hover:bg-gray-100'
        } ${!canHide ? 'opacity-50 cursor-not-allowed' : ''}`}
        title={config.visible ? 'Ukryj kolumnę' : 'Pokaż kolumnę'}
      >
        {config.visible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
      </button>

      {/* Column label */}
      <input
        type="text"
        value={config.customLabel ?? column.header}
        onChange={(e) => onUpdateLabel(e.target.value)}
        placeholder={column.header}
        className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
      />

      {/* Width input */}
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={config.width === 'auto' ? '' : config.width}
          onChange={(e) => onUpdateWidth(e.target.value)}
          placeholder="auto"
          className="w-20 px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
        />
        <span className="text-xs text-gray-400">px/%</span>
      </div>
    </div>
  );
}
